import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { API_recursos } from "../../config/apiConfig";
import Navbar from "../../components/Navbar";
import ImageUploader from "../../components/ImageUploader";
import "./css/NuevoEmpleo.css";

const NuevoRecursoInteres = () => {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [link, setLink] = useState("");
  const [categoria, setCategoria] = useState("");
  const [tipoSocial, setTipoSocial] = useState(""); // Nuevo estado para el tipo específico de recurso social
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [destacado, setDestacado] = useState(false);
  const [imagePath, setImagePath] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);

  const { id } = useParams(); // si existe, estamos en modo edición
  const navigate = useNavigate();

  useEffect(() => {
    const cargarDatos = async () => {
      if (id) {
        try {
          // Usar el endpoint específico para obtener un recurso por ID
          const res = await fetch(`${API_recursos}/${id}`);
          if (!res.ok) {
            throw new Error("Recurso no encontrado");
          }
          const recurso = await res.json();

          setTitulo(recurso.title || "");
          setDescripcion(recurso.description || "");
          setLink(recurso.link || "");

          // Manejar la categoría y tipo social
          const categoriaRecurso = recurso.category.toLowerCase();
          if (categoriaRecurso === "otros") {
            setCategoria("recurso");
          } else if (["subvenciones", "prestaciones", "vivienda"].includes(categoriaRecurso)) {
            setCategoria("social");
            setTipoSocial(categoriaRecurso.charAt(0).toUpperCase() + categoriaRecurso.slice(1));
          } else {
            setCategoria(categoriaRecurso);
          }

          setFechaInicio(recurso.featuredStart?.slice(0, 10) || "");
          setFechaFin(recurso.featuredEnd?.slice(0, 10) || "");
          setDestacado(recurso.isFeatured || false);
          setImagePath(recurso.imagePath || null);
        } catch (error) {
          console.error("Error al cargar recurso:", error);
          toast.error("Error al cargar el recurso");
        }
      }
    };

    cargarDatos();
  }, [id]);

  const handleCrearRecurso = async () => {
    // Campos obligatorios
    if (!titulo || !descripcion || !categoria) {
      toast.error("Hay que rellenar el título, descripción y categoría.");
      return;
    }

    // Validación adicional para recursos sociales
    if (categoria === "social" && !tipoSocial) {
      toast.error("Debe seleccionar el tipo de recurso social.");
      return;
    }

    try {
      const categoriaMapeada = {
        empleo: "empleo",
        formacion: "formacion",
        social: tipoSocial.toLowerCase(), // Usar el tipo específico en lugar de "social"
        recurso: "otros",
      };

      const body = {
        title: titulo,
        description: descripcion,
        isFeatured: destacado,
        category: categoriaMapeada[categoria] || "otros",
        imagePath: imagePath,
        availableUntil: "2099-12-31T23:59:59.000Z",
        link: link || "",
        featuredStart: fechaInicio || null,
        featuredEnd: fechaFin || null,
        location: "No especificado",
        createdAt: new Date().toISOString(),
        modality: null,
        institutionalFacility: null,
        duration: null,
      };

      const url = id ? `${API_recursos}/${id}` : API_recursos;
      const method = id ? "PUT" : "POST";

      const response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error("Error al guardar el recurso");
      }

      toast.success(`Recurso ${id ? "actualizado" : "creado"} correctamente`, {
        autoClose: 2000,
      });
      setTimeout(() => navigate("/admin/recursos"), 2000);
    } catch (error) {
      console.error("Error al enviar el recurso:", error);
      toast.error("Hubo un error al guardar el recurso");
    }
  };

  const handleCancelar = () => setMostrarModal(true);
  const handleSalir = () => navigate("/admin/recursos");

  return (
    <>
      <Navbar />

      <main className="main creacion-ofertas">
        <section className="cabecera between">
          <h1 className="titulo-user align-center">{id ? "EDITAR RECURSO DE INTERÉS" : "NUEVO RECURSO DE INTERÉS"}</h1>

          <div className="fechas-inscripcion align-center">
            <p className="verde">FECHAS DE VISUALIZACIÓN:</p>
            <input type="date" value={fechaInicio} aria-label="Fecha de Inicio" onChange={(e) => setFechaInicio(e.target.value)} />
            <p>-</p>
            <input type="date" value={fechaFin} aria-label="Fecha fin" onChange={(e) => setFechaFin(e.target.value)} />
          </div>

          <div className="noticias-destacadas align-center">
            <input type="checkbox" id="destacado" checked={destacado} onChange={(e) => setDestacado(e.target.checked)} />
            <label htmlFor="destacado"> Colocar en Destacados</label>
          </div>
        </section>

        <section className="formulario-alta between">
          <div className="comple-recurso column gap-30 w-450">
            <div className="campo column">
              <label className="verde mayusculas">Título del recurso</label>
              <input type="text" placeholder="Título" value={titulo} onChange={(e) => setTitulo(e.target.value)} />
            </div>

            <ImageUploader imageUrl={imagePath} onImageChange={setImagePath} folder="recursos" label="Subir imagen" accept="image/*" />
          </div>

          <div className="info-recurso column gap-30 w-450">
            <div className="campo column">
              <label className="verde mayusculas">Categoría del recurso</label>
              <div className="flex gap-20 mt-5">
                {["empleo", "formacion", "social", "recurso"].map((tipo) => (
                  <div className="checkbox flex gap-5" key={tipo}>
                    <input
                      type="radio"
                      name="categoria"
                      value={tipo}
                      aria-label={tipo}
                      checked={categoria === tipo}
                      onChange={(e) => {
                        setCategoria(e.target.value);
                        // Limpiar tipo social si se cambia de categoría
                        if (e.target.value !== "social") {
                          setTipoSocial("");
                        }
                      }}
                    />
                    <label htmlFor={tipo}>{tipo === "recurso" ? "Recurso general" : tipo.charAt(0).toUpperCase() + tipo.slice(1)}</label>
                  </div>
                ))}
              </div>

              {/* Selector de tipo social - solo visible cuando se selecciona "social" */}
              {categoria === "social" && (
                <div className="campo column mt-10">
                  <label className="verde mayusculas">Tipo de recurso social *</label>
                  <div className="flex gap-20 mt-5">
                    {["Subvenciones", "Prestaciones", "Vivienda"].map((tipo) => (
                      <div className="checkbox flex gap-5" key={tipo}>
                        <input
                          type="radio"
                          name="tipoSocial"
                          value={tipo}
                          aria-label={tipo}
                          checked={tipoSocial === tipo}
                          onChange={(e) => setTipoSocial(e.target.value)}
                        />
                        <label htmlFor={tipo}>{tipo}</label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="campo column">
              <label className="verde mayusculas">Link del recurso</label>
              <input
                type="text"
                placeholder="https://www.ejemplo.com"
                value={link}
                aria-label="Enlace para más información sobre el recurso"
                onChange={(e) => setLink(e.target.value)}
              />
            </div>

            <div className="campo column sobre-mi">
              <label className="verde mayusculas">INFORMACIÓN DEL RECURSO</label>
              <textarea
                autoCorrect="off"
                placeholder="Descripción..."
                value={descripcion}
                aria-label="Información del recurso"
                onChange={(e) => setDescripcion(e.target.value)}
              />
            </div>
          </div>
        </section>

        {mostrarModal && (
          <div className="ventana-cancelar fondo-blanco">
            <div className="column">
              <p>
                Si sales de esta página no se guardará el recurso.
                <br />
                ¿Estás seguro que quieres salir?
              </p>
              <div className="botones flex gap-20">
                <button className="fondo-rojo blanco" onClick={handleSalir}>
                  Salir
                </button>
                <button className="seguir blanco fondo-verde" onClick={() => setMostrarModal(false)}>
                  Seguir
                </button>
              </div>
            </div>
          </div>
        )}

        <section className="botones flex-center">
          <button className="fondo-rojo mayusculas blanco" onClick={handleCancelar}>
            Cancelar
          </button>
          <button className="descargar mayusculas fondo-verde blanco" onClick={handleCrearRecurso}>
            {id ? "Guardar cambios" : "Crear nuevo recurso"}
          </button>
        </section>
      </main>
    </>
  );
};

export default NuevoRecursoInteres;
